import { useGateValue } from '@statsig/react-bindings';

import { useGetWorkspace } from '@/generated/hooks/agora/v1/workspace';
import { isFinanceWorkspaceType } from '@/shared/utils/finance-workspace';
import { FEATURE_FLAGS } from '@/shared/types/features';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

export interface IUseIsFinanceWorkspaceResult {
  /** True only once the workspace has resolved AND its type is finance. */
  isFinanceWorkspace: boolean;
  /**
   * True while the workspace lookup is still in flight — the determination is
   * unknown. Callers should treat this as "don't decide yet" rather than
   * reading `isFinanceWorkspace === false` (the default during load) as
   * authoritative.
   */
  isLoading: boolean;
  /** True when the workspace lookup failed, so the type can't be confirmed. */
  isError: boolean;
  /** The lookup error, when `isError` is true. */
  error: unknown;
}

/**
 * Single source of truth for "is the finance experience active here?": owns the
 * `enable_finance_workspace` Statsig gate AND the `workspace_type` check AND the
 * GetWorkspace query options, so every caller (the sidebar nav, the route-level
 * FinanceLayout) shares one gated determination. Folding the gate in here is
 * deliberate: it makes it impossible to surface finance chrome or reach a
 * finance route without the gate — a new caller (or a new sidebar link / page)
 * inherits the guard for free, with nothing extra to remember to wire up.
 *
 * By default it resolves the CURRENT workspace from the user-preferences store.
 * Pass `organizationId`/`workspaceId` explicitly — e.g. from the URL, the
 * canonical source on a workspace route — when the store would lag the URL
 * during a workspace switch and make the check misfire mid-switch.
 *
 * Fetches the workspace directly by id rather than scanning the "my workspaces"
 * membership list (the seeded finance workspace isn't necessarily in it). The
 * result is cached (staleTime) so the determination doesn't refetch — and the
 * nav doesn't flicker — on every remount / SPA navigation within the workspace.
 * When the gate is off the query is disabled entirely: the answer is already
 * "no", so there is nothing to resolve and nothing to wait on.
 *
 * Pass `options.enabled: false` to additionally suppress the lookup in
 * scenarios where the answer isn't needed yet — e.g. ProtectedRoute only
 * resolves the landing workspace while redirecting from home. The gate is
 * still folded in either way, so callers can't opt out of the gate itself.
 */
export function useIsFinanceWorkspace(
  organizationId?: string,
  workspaceId?: string,
  options?: { enabled?: boolean },
): IUseIsFinanceWorkspaceResult {
  const isFinanceGateEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_FINANCE_WORKSPACE,
  );

  const storeOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const storeWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const resolvedOrganizationId = organizationId ?? storeOrganizationId;
  const resolvedWorkspaceId = workspaceId ?? storeWorkspaceId;

  const enabled =
    (options?.enabled ?? true) &&
    isFinanceGateEnabled &&
    !!resolvedOrganizationId &&
    !!resolvedWorkspaceId;
  const {
    data: workspaceData,
    isLoading,
    isError,
    error,
  } = useGetWorkspace(resolvedOrganizationId, resolvedWorkspaceId, {
    query: { enabled, staleTime: 5 * 60 * 1000, retry: false },
  });

  return {
    isFinanceWorkspace:
      isFinanceGateEnabled &&
      isFinanceWorkspaceType(workspaceData?.data?.workspace_type),
    isLoading: enabled && isLoading,
    isError: enabled && isError,
    error,
  };
}
